// client/src/components/indicadores/ServicosReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useServicosReport } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { ClipboardList, DollarSign, TrendingUp, Users } from 'lucide-react';
import ReportPageLayout from './ReportPageLayout';
import ReportTable from './ReportTable';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE
const initialFilters = {
  startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  endDate: new Date(),
};

const ServicosReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    // ✅ ACHADO: relatorio fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();

    const [appliedFilters, setAppliedFilters] = useState(initialFilters);
    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: [],
        servicos: [],
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // ✅ USANDO O NOVO HOOK
    const { data: reportData = [], isLoading, error } = useServicosReport(appliedFilters);

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de serviços:', error);
            
            let errorMessage = 'Ocorreu um erro ao buscar o relatório de serviços.';
            
            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // ✅ CARREGAR OPÇÕES DE FILTRO - PADRONIZADO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin e
            // matriz_admin nunca viam essas opções de filtro. Os endpoints
            // já escopam sozinhos pelo JWT (filterByUserUnits).
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de serviços', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional' } }),
                    // Serviços
                    api.get('/servicos', { params: baseParams })
                ];

                console.log('📡 Fazendo requisições para filtros...');
                
                const [unidadesRes, profissionaisRes, servicosRes] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de serviços carregados com sucesso:', {
                    unidades: filterData.unidades.length,
                    profissionais: filterData.profissionais.length,
                    servicos: filterData.servicos.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de serviços:', error);
                
                let errorMessage = 'Erro ao carregar opções de filtro.';
                
                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                setAlert(errorMessage, 'danger');
                
                // ✅ FALLBACK: Definir arrays vazios para evitar quebra da UI
                setFilterOptions({
                    unidades: [],
                    profissionais: [],
                    servicos: [],
                });
            } finally {
                setLoadingFilters(false);
            }
        };
        fetchFilterOptions();
    }, [user, scope, setAlert]);

    // ✅ KPIs DINÂMICOS
    const kpiCards = [
        { 
            title: "Total de Serviços", 
            value: (reportData?.length || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: ClipboardList,
            description: "Serviços com agendamentos"
        },
        {
            title: "Faturamento Total",
            value: formatCurrency(reportData?.reduce((sum, item) => sum + (item.faturamentoTotal || 0), 0) || 0, currency, locale),
            prefix: "",
            icon: DollarSign,
            description: "Valor total faturado"
        },
        { 
            title: "Agendamentos", 
            value: (reportData?.reduce((sum, item) => sum + (item.totalAgendamentos || 0), 0) || 0).toLocaleString('pt-BR'), 
            prefix: "",
            icon: TrendingUp,
            description: "Total de agendamentos"
        },
        {
            title: "Ticket Médio",
            value: formatCurrency(reportData?.length > 0
                ? (reportData.reduce((sum, item) => sum + (item.faturamentoTotal || 0), 0) / reportData.reduce((sum, item) => sum + (item.totalAgendamentos || 0), 1))
                : 0, currency, locale),
            prefix: "",
            icon: Users,
            description: "Valor médio por agendamento"
        }
    ];

    // ✅ COLUNAS DA TABELA MELHORADAS
    const columns = [
        { 
            key: 'nome', 
            header: 'Serviço', 
            render: (row) => (
                <div className="d-flex align-items-center">
                    <ClipboardList size={16} className="me-2 text-muted" />
                    <span className="fw-semibold">{row.nome || 'Serviço não informado'}</span>
                </div>
            ),
            sortable: true
        },
        { 
            key: 'totalAgendamentos', 
            header: 'Agendamentos', 
            render: (row) => (
                <span className="fw-bold text-primary">
                    {(row.totalAgendamentos || 0).toLocaleString('pt-BR')}
                </span>
            ),
            sortable: true
        },
        { 
            key: 'faturamentoTotal', 
            header: 'Faturamento Total', 
            render: (row) => (
                <span className="fw-bold text-success">
                    {formatCurrency(row.faturamentoTotal || 0, currency, locale)}
                </span>
            ),
            sortable: true
        },
        { 
            key: 'ticketMedio', 
            header: 'Ticket Médio', 
            render: (row) => {
                const agendamentos = row.totalAgendamentos || 0;
                const faturamento = row.faturamentoTotal || 0;
                const ticketMedio = agendamentos > 0 ? faturamento / agendamentos : 0;
                
                return (
                    <span className="fw-semibold">
                        {formatCurrency(ticketMedio, currency, locale)}
                    </span>
                );
            },
            sortable: true
        },
        { 
            key: 'percentual', 
            header: 'Participação', 
            render: (row) => {
                const totalFaturamento = reportData?.reduce((sum, item) => sum + (item.faturamentoTotal || 0), 0) || 1;
                const percentual = ((row.faturamentoTotal || 0) / totalFaturamento) * 100;
                
                return (
                    <span className={`badge ${
                        percentual >= 20 ? 'bg-success' : 
                        percentual >= 10 ? 'bg-primary' : 'bg-secondary'
                    }`}>
                        {percentual.toFixed(1)}%
                    </span>
                );
            },
            sortable: true
        }
    ];

    // ✅ CONFIGURAÇÕES DAS TABELAS
    const tableConfigurations = [
        {
            title: "Desempenho dos Serviços",
            columns: columns,
            data: reportData,
            defaultSort: 'faturamentoTotal',
            defaultSortDirection: 'desc'
        }
    ];

    // ✅ ESTATÍSTICAS ADICIONAIS
    const getServiceStats = () => {
        if (!reportData || reportData.length === 0) return null;

        const totalFaturamento = reportData.reduce((sum, item) => sum + (item.faturamentoTotal || 0), 0);
        const totalAgendamentos = reportData.reduce((sum, item) => sum + (item.totalAgendamentos || 0), 0);
        
        // Serviço mais popular
        const servicoMaisPopular = reportData.reduce((prev, current) => 
            (prev.totalAgendamentos > current.totalAgendamentos) ? prev : current
        );

        // Serviço com maior faturamento
        const servicoMaiorFaturamento = reportData.reduce((prev, current) => 
            (prev.faturamentoTotal > current.faturamentoTotal) ? prev : current
        );

        // Serviço com maior ticket médio
        const servicoMaiorTicket = reportData.reduce((prev, current) => {
            const prevTicket = (prev.totalAgendamentos || 0) > 0 ? (prev.faturamentoTotal || 0) / (prev.totalAgendamentos || 1) : 0;
            const currentTicket = (current.totalAgendamentos || 0) > 0 ? (current.faturamentoTotal || 0) / (current.totalAgendamentos || 1) : 0;
            return prevTicket > currentTicket ? prev : current;
        });

        return {
            totalFaturamento,
            totalAgendamentos,
            servicoMaisPopular,
            servicoMaiorFaturamento,
            servicoMaiorTicket,
            ticketMedioGeral: totalAgendamentos > 0 ? totalFaturamento / totalAgendamentos : 0
        };
    };

    const serviceStats = getServiceStats();

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // ✅ LOADING STATE MELHORADO
    if (loadingFilters) {
        return (
            <div className="content-panel">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-3 text-muted">Carregando opções de filtro...</p>
                </div>
            </div>
        );
    }

    return (
        <ReportPageLayout
            icon={<ClipboardList size={24} />}
            title="Relatório de Serviços"
            onApplyFilters={setAppliedFilters}
            filterOptions={filterOptions}
            enabledFilters={['date', 'profissional', 'unidade', 'servico']}
            isLoading={isLoading || loadingFilters}
            kpiCards={kpiCards}
            tableConfigurations={tableConfigurations}
            initialFilters={initialFilters}
        >
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ TABELA DE DESEMPENHO — tableConfigurations era montado mas
                nunca renderizado (ReportPageLayout não usa essa prop, só
                `children`), então a tabela nunca aparecia na tela. */}
            {!isBackendUnavailable && tableConfigurations.map((config) => (
                <ReportTable
                    key={config.title}
                    title={config.title}
                    columns={config.columns}
                    data={config.data}
                    isLoading={isLoading}
                />
            ))}

            {/* ✅ ANÁLISE DETALHADA */}
            {!isBackendUnavailable && serviceStats && (
                <div className="content-panel mb-4">
                    <h4 className="panel-title">Análise dos Serviços</h4>
                    <div className="row">
                        <div className="col-md-6">
                            <div className="stat-detail-card">
                                <h6>Destaques</h6>
                                {serviceStats.servicoMaisPopular && (
                                    <div className="stat-detail-item">
                                        <span>Serviço Mais Popular:</span>
                                        <strong>{serviceStats.servicoMaisPopular.nome} ({serviceStats.servicoMaisPopular.totalAgendamentos} agend.)</strong>
                                    </div>
                                )}
                                {serviceStats.servicoMaiorFaturamento && (
                                    <div className="stat-detail-item">
                                        <span>Maior Faturamento:</span>
                                        <strong>{serviceStats.servicoMaiorFaturamento.nome} ({formatCurrency(serviceStats.servicoMaiorFaturamento.faturamentoTotal || 0, currency, locale)})</strong>
                                    </div>
                                )}
                                {serviceStats.servicoMaiorTicket && (
                                    <div className="stat-detail-item">
                                        <span>Maior Ticket Médio:</span>
                                        <strong>{serviceStats.servicoMaiorTicket.nome} ({formatCurrency(
                                            (serviceStats.servicoMaiorTicket.totalAgendamentos || 0) > 0
                                                ? (serviceStats.servicoMaiorTicket.faturamentoTotal || 0) / (serviceStats.servicoMaiorTicket.totalAgendamentos || 1)
                                                : 0,
                                            currency, locale
                                        )})</strong>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="stat-detail-card">
                                <h6>Métricas Gerais</h6>
                                <div className="stat-detail-item">
                                    <span>Ticket Médio Geral:</span>
                                    <strong>{formatCurrency(serviceStats.ticketMedioGeral, currency, locale)}</strong>
                                </div>
                                <div className="stat-detail-item">
                                    <span>Agendamentos por Serviço (média):</span>
                                    <strong>{(serviceStats.totalAgendamentos / reportData.length).toFixed(1)}</strong>
                                </div>
                                <div className="stat-detail-item">
                                    <span>Faturamento por Serviço (média):</span>
                                    <strong>{formatCurrency(serviceStats.totalFaturamento / reportData.length, currency, locale)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ ESTADO VAZIO MELHORADO */}
            {!isBackendUnavailable && !isLoading && reportData.length === 0 && (
                <div className="empty-state text-center py-5">
                    <ClipboardList size={48} className="text-muted mb-3" />
                    <h5>Nenhum serviço encontrado</h5>
                    <p className="text-muted">
                        Não foram encontrados dados de serviços para os filtros selecionados.
                        Verifique se há agendamentos realizados no período.
                    </p>
                </div>
            )}
        </ReportPageLayout>
    );
};

export default ServicosReport;